// src/components/LoadingScreen.js

import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, LinearProgress, Backdrop, Fade, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { LOADING_STEPS } from '../config/loadingSteps';
import { loadingScreenStyles, getLoadingColor, designTokens } from '../theme/designTokens';
import { bg } from '../utils/darkModeAdapter';

/**
 * 統一ローディング画面コンポーネント
 *
 * @param {string} variant - 表示バリエーション: 'fullscreen' | 'overlay' | 'inline'
 * @param {string} message - 表示メッセージ
 * @param {string} color - アクセントカラー
 * @param {string} backgroundColor - 背景色（fullscreen時）
 * @param {React.Component} IconComponent - 表示アイコン
 * @param {string} loadingType - ステップ表示用のタイプ
 * @param {boolean} showSteps - ステップ表示の有無
 * @param {boolean} showProgress - プログレスバー表示の有無
 * @param {boolean} showCounter - ステップカウンター表示の有無
 * @param {string} featureKey - 機能別テーマカラー用キー
 * @param {string} description - 詳細説明（overlay時）
 */
export default function LoadingScreen({
  variant = 'fullscreen',
  message = 'AIが思考中...',
  color,
  backgroundColor,
  IconComponent = PsychologyIcon,
  loadingType,
  showSteps = true,
  showProgress = true,
  showCounter = true,
  featureKey = null,
  description = '',
}) {
  const muiTheme = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [isOvertime, setIsOvertime] = useState(false);

  // 色の決定（featureKey > color > デフォルト）
  const accentColor = useMemo(() => {
    if (featureKey) return getLoadingColor(featureKey);
    return color || '#249BE0';
  }, [featureKey, color]);

  // loadingTypeに応じたステップを取得（overlay時は_regenerateサフィックス対応）
  const adjustedLoadingType = useMemo(() => {
    if (!loadingType) return null;
    if (variant === 'overlay' && !loadingType.endsWith('_regenerate')) {
      return `${loadingType}_regenerate`;
    }
    return loadingType;
  }, [loadingType, variant]);

  const steps = useMemo(() => {
    if (!showSteps || !adjustedLoadingType) return null;
    return LOADING_STEPS[adjustedLoadingType]?.steps || LOADING_STEPS.default?.steps || null;
  }, [adjustedLoadingType, showSteps]);

  // ステップの自動進行
  useEffect(() => {
    if (!steps || steps.length === 0) return;

    if (currentStep >= steps.length - 1) {
      const overtimeTimer = setTimeout(() => {
        setIsOvertime(true);
      }, steps[currentStep].duration + 5000);

      return () => clearTimeout(overtimeTimer);
    }

    const timer = setTimeout(() => {
      setCurrentStep(prev => prev + 1);
    }, steps[currentStep].duration);

    return () => clearTimeout(timer);
  }, [currentStep, steps]);

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      setCurrentStep(0);
      setIsOvertime(false);
    };
  }, []);

  // 進捗率の計算
  const progressValue = useMemo(() => {
    if (!steps || steps.length === 0) return 0;
    return ((currentStep + 1) / steps.length) * 100;
  }, [currentStep, steps]);

  // 表示するメッセージ
  const displayMessage = useMemo(() => {
    if (steps && steps[currentStep]) {
      return steps[currentStep].message;
    }
    return message;
  }, [steps, currentStep, message]);

  // アニメーション設定（控えめ）
  const scaleMotion = {
    animate: {
      scale: loadingScreenStyles.animation.scale,
    },
    transition: {
      duration: loadingScreenStyles.animation.duration,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  };

  // 共通のコンテンツ部分
  const renderContent = (isOverlay = false) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: isOverlay ? 3 : 4,
      }}
    >
      {/* アイコン */}
      {isOverlay ? (
        <CircularProgress
          size={56}
          thickness={4}
          sx={{ color: accentColor }}
        />
      ) : (
        <Box
          component={motion.div}
          {...scaleMotion}
          sx={loadingScreenStyles.iconContainer(accentColor)}
        >
          <IconComponent sx={{ fontSize: 56, color: accentColor }} />
        </Box>
      )}

      {/* メッセージ表示 */}
      <Box sx={{ minHeight: '48px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: loadingScreenStyles.animation.transition }}
          >
            <Typography
              variant={isOverlay ? 'h6' : 'h5'}
              sx={loadingScreenStyles.message}
            >
              {displayMessage}
            </Typography>
          </motion.div>
        </AnimatePresence>

        {/* 説明文（overlay時、ステップがない場合） */}
        {description && !steps && isOverlay && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, textAlign: 'center' }}
          >
            {description}
          </Typography>
        )}
      </Box>

      {/* 進捗バーとステップ情報 */}
      {showSteps && steps && (
        <Box sx={{ width: isOverlay ? '100%' : '360px', mt: 1 }}>
          {showProgress && (
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{
                ...loadingScreenStyles.progressBar(accentColor),
                mb: 1.5,
              }}
            />
          )}

          {showCounter && (
            <Typography
              variant="body2"
              sx={loadingScreenStyles.stepCounter}
            >
              ステップ {currentStep + 1} / {steps.length}
            </Typography>
          )}

          {isOvertime && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              <Typography
                variant="caption"
                sx={loadingScreenStyles.overtimeMessage}
              >
                通常より時間がかかっています。もう少しお待ちください...
              </Typography>
            </motion.div>
          )}
        </Box>
      )}
    </Box>
  );

  // オーバーレイ表示
  if (variant === 'overlay') {
    return (
      <Backdrop
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          ...loadingScreenStyles.overlayBackdrop,
          backgroundColor: muiTheme.palette.mode === 'dark' ? 'rgba(18,18,18,0.85)' : 'rgba(255,255,255,0.8)',
          flexDirection: 'column',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        open={true}
      >
        <Fade in={true} timeout={600}>
          <Box sx={{ ...loadingScreenStyles.overlayCard, backgroundColor: muiTheme.palette.mode === 'dark' ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.95)' }}>
            {renderContent(true)}
          </Box>
        </Fade>
      </Backdrop>
    );
  }

  // インライン表示
  if (variant === 'inline') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
        }}
      >
        {renderContent(true)}
      </Box>
    );
  }

  // フルスクリーン表示（デフォルト）
  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        backgroundColor: backgroundColor || bg(muiTheme).card,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {renderContent(false)}
    </Box>
  );
}
